// Client-side history of recent *content* search queries (strings the user
// actually searched for, not navigation/external/quick-action selections).
// Surfaced in the top-nav dropdown empty state. Stored in localStorage so it
// survives reloads; cleared on logout (see actions/logoutInternal.js).

import Fuse from 'fuse.js'

const STORAGE_KEY = 'recentSearchQueries'

// How many of the newest recent searches are shown in the dropdown empty state
// (the "prefill"). The complete history is kept in storage and made searchable
// via searchRecentSearches() while the user types.
export const RECENT_SEARCHES_PREFILL_COUNT = 3

// Fuse options for matching the user's query against their recent-search
// history. minMatchCharLength 1 so a single typed character can surface a
// recent search (consistent with the other local searches).
const RECENT_SEARCHES_FUSE_OPTIONS = {
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
}

/**
 * Return the stored recent searches, newest first. Tolerates missing/corrupt
 * data by returning an empty array.
 */
export function getRecentSearches() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((q) => typeof q === 'string')
  } catch (e) {
    return []
  }
}

/**
 * Return the newest `count` recent searches for the dropdown empty-state
 * prefill. Defaults to RECENT_SEARCHES_PREFILL_COUNT.
 */
export function getRecentSearchesForPrefill(count = RECENT_SEARCHES_PREFILL_COUNT) {
  return getRecentSearches().slice(0, count)
}

/**
 * Record a query at the head of the recent-search list. Trims whitespace,
 * ignores empty strings, de-dupes case-insensitively (keeping the newest
 * casing). The complete history is retained (no cap) so it can be searched.
 */
export function recordRecentSearch(query) {
  const trimmed = (query || '').trim()
  if (!trimmed) return getRecentSearches()
  const existing = getRecentSearches()
  const lower = trimmed.toLowerCase()
  const deduped = existing.filter((q) => q.toLowerCase() !== lower)
  const next = [trimmed, ...deduped]
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (e) {
    // Storage full / unavailable — non-fatal, just skip persisting.
  }
  return next
}

/**
 * Remove a single recent search (case-insensitive match) and return the
 * updated list. Used by the per-row delete (X) affordance in the dropdown.
 */
export function removeRecentSearch(query) {
  const trimmed = (query || '').trim()
  if (!trimmed) return getRecentSearches()
  const lower = trimmed.toLowerCase()
  const next = getRecentSearches().filter((q) => q.toLowerCase() !== lower)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (e) {
    // non-fatal
  }
  return next
}

/**
 * Fuzzy-search the complete recent-search history for the given query. Returns
 * matching recent-search strings, newest-first within equal relevance. Used to
 * surface relevant past searches as the first dropdown section while typing.
 */
export function searchRecentSearches(query, limit = 5) {
  const trimmed = (query || '').trim()
  const all = getRecentSearches()
  if (!trimmed || all.length === 0) return []
  const fuse = new Fuse(all, RECENT_SEARCHES_FUSE_OPTIONS)
  return fuse.search(trimmed, { limit }).map((r) => r.item)
}

/**
 * Remove all stored recent searches.
 */
export function clearRecentSearches() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // non-fatal
  }
}
